import React from "react";
import styles from "./track.module.scss";
import classnames from "classnames/bind";
import { RADIO_STATES, Radio } from "../../Common/Radio";
import { DeleteButton } from "../../Common/DeleteButton";
import { useTheme } from "../../../contexts/ThemeContext";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { ItemWithInput } from "components/Common/ItemWithInput";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import { isNil, sum } from "lodash";
import { SmallStepperButton } from "components/Common/SmallStepperButton";

const classNameBuilder = classnames.bind(styles);

export const Track = ({
  label,
  customLabel,
  value = [],
  breaks = {},
  setValueByIndex,
  setBreakByIndex,
  className,
  dotsClassName,
  inline,
  shouldRenderBeforeTrack,
  onReset,
  onIncrease,
  onReduce,
  onDelete,
  disableBurned,
  radioStates: radioStatesProp,
  disableBreaks,
  disableWrap,
  smallTrack,
  shouldConfirmDelete,
  maxLength,
  minLength = 1,
  alwaysVisibleUpdateButtons,
  presentation,
  dividerClassName,
  colorless,
}) => {
  const { themeClassNames } = useTheme();
  const max = value.length || 0;
  const radioStates =
    radioStatesProp ||
    (disableBurned
      ? [RADIO_STATES.EMPTY, RADIO_STATES.MARKED]
      : [RADIO_STATES.EMPTY, RADIO_STATES.MARKED, RADIO_STATES.BURNED]);
  const onMark = (e, newValue, index) => {
    setValueByIndex(newValue, index);
  };
  const canBreak = !disableBreaks && setBreakByIndex;

  let beforeTrack;
  let afterTrack;

  const buttonContent = (
    <>
      {!presentation && (onReduce || onIncrease || onReset) && (
        <div
          className={classNameBuilder("update-button-container", {
            visible: alwaysVisibleUpdateButtons,
            before: shouldRenderBeforeTrack,
          })}
        >
          {onReset && (
            <SmallStepperButton
              onClick={onReset}
              disabled={sum(value) === 0}
              tooltip={<Tooltip>{"Clear all marks and burns"}</Tooltip>}
            >
              <RotateCcw />
            </SmallStepperButton>
          )}
          {onReduce && (
            <SmallStepperButton onClick={onReduce} disabled={max <= minLength}>
              <Minus />
            </SmallStepperButton>
          )}
          {onIncrease && (
            <SmallStepperButton
              onClick={onIncrease}
              disabled={!isNil(maxLength) && max >= maxLength}
            >
              <Plus />
            </SmallStepperButton>
          )}
        </div>
      )}
    </>
  );

  if (shouldRenderBeforeTrack) {
    beforeTrack = buttonContent;
  } else {
    afterTrack = buttonContent;
  }
  return (
    <div
      className={classNameBuilder("container", ...themeClassNames, className, {
        presentation,
        inline,
      })}
    >
      {(label || customLabel) && (
        <div className={styles["label"]}>
          {customLabel || <span>{label}</span>}
          <DeleteButton
            className={styles["delete-button"]}
            iconClassName={styles["icon"]}
            onClick={onDelete}
            title="Delete Track"
            name={label}
            shouldConfirm={shouldConfirmDelete}
          />
        </div>
      )}
      <div
        className={classNameBuilder("content", {
          "small-track": smallTrack,
        })}
      >
        <div
          className={classNameBuilder("dots", dotsClassName, {
            "no-wrap": disableWrap,
          })}
        >
          {beforeTrack}
          {value.map((v, i) => {
            const dividerSelected = v !== RADIO_STATES.EMPTY;
            const nextDividerSelected = value[i + 1] !== RADIO_STATES.EMPTY;
            const dividerBurned =
              v === RADIO_STATES.BURNED || value[i + 1] === RADIO_STATES.BURNED;
            const hasBreak = breaks[i];

            return (
              <React.Fragment key={i}>
                <Radio
                  value={v}
                  states={radioStates}
                  onClick={(e, nextValue) => onMark(e, nextValue, i)}
                  colorless={colorless}
                />
                {i !== value.length - 1 && (
                  <div
                    className={classNameBuilder(
                      "divider-with-tooltip",
                      dividerClassName,
                      { presentation }
                    )}
                  >
                    <ItemWithInput
                      disabled={!canBreak || presentation}
                      className={classNameBuilder(
                        "divider-container",
                        ...themeClassNames,
                        {
                          hover: canBreak,
                          long: hasBreak,
                          presentation: presentation || !canBreak,
                        }
                      )}
                      onClick={hasBreak && (() => setBreakByIndex(false, i))}
                      inputProps={{
                        placeholder: "Break name",
                        onSubmit: (name) => setBreakByIndex(name || " ", i),
                        title: `Submit new break`,
                      }}
                      tooltipProps={{
                        position: "bottom",
                      }}
                    >
                      <div
                        className={classNameBuilder("break", {
                          hidden: !hasBreak || !canBreak,
                          selected: dividerSelected,
                          colorless,
                          presentation: presentation || !canBreak,
                        })}
                      />
                      <div
                        className={classNameBuilder("half-divider", {
                          selected: dividerSelected && hasBreak,
                          hidden:
                            !(dividerSelected && hasBreak) || dividerBurned,
                          colorless,
                          presentation: presentation || !canBreak,
                        })}
                      />
                      <div
                        className={classNameBuilder("divider", {
                          selected: dividerSelected && nextDividerSelected,
                          hidden: dividerBurned,
                          colorless,
                          presentation: presentation || !canBreak,
                        })}
                      />
                    </ItemWithInput>
                    {hasBreak && canBreak && <Tooltip>{breaks[i]}</Tooltip>}
                    {!hasBreak && !presentation && canBreak && (
                      <Tooltip>Add Break</Tooltip>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {afterTrack}
        </div>
      </div>
    </div>
  );
};
